import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  User, Lock, Bell, CreditCard, Shield, LogOut,
  Eye, EyeOff, Check, ChevronRight, X, Download,
  AlertTriangle, ExternalLink, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCookieConsent } from '../context/CookieConsentContext';
import i18n from '../i18n';

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_COLORS = {
  free: '#a0a0ab', explorateur: '#a0a0ab',
  solo: '#ff6b6b', famille: '#20c997',
  organisation: '#ffc107', createur_pro: '#ff6b6b',
};

const SOCIAL_PROVIDERS = [
  { key: 'google', label: 'Google', icon: '🔵' },
  { key: 'microsoft', label: 'Microsoft', icon: '🪟' },
  { key: 'facebook', label: 'Facebook', icon: '🔷' },
];

// ── Toggle component ───────────────────────────────────────────────────────
function Toggle({ checked, onChange, testId }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`relative inline-flex w-10 h-5.5 items-center rounded-full transition-colors duration-200 focus:outline-none ${checked ? 'bg-[#ff6b6b]' : 'bg-white/15'}`}
      style={{ minWidth: 40, height: 22 }}
    >
      <span
        className={`inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 ${checked ? 'translate-x-5' : 'translate-x-1'}`}
      />
    </button>
  );
}

// ── Section wrapper ────────────────────────────────────────────────────────
function Section({ icon: Icon, title, children, testId }) {
  return (
    <div className="kurso-card p-5 animate-fade-up" data-testid={testId}>
      <h2 className="font-heading text-base font-semibold text-white mb-4 flex items-center gap-2">
        <Icon size={16} className="text-[#ff6b6b]" />
        {title}
      </h2>
      {children}
    </div>
  );
}

// ── Delete Account Modal ───────────────────────────────────────────────────
function DeleteAccountModal({ email, onConfirm, onClose, loading, t }) {
  const [emailInput, setEmailInput] = useState('');
  const valid = emailInput.trim().toLowerCase() === email.toLowerCase();
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm" data-testid="delete-modal">
      <div className="w-full max-w-sm bg-[#1a1927] border border-[#ff6b6b]/30 rounded-2xl p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-[#ff6b6b]" />
            <h3 className="font-heading font-bold text-white">{t('settings.deleteAccountConfirmTitle')}</h3>
          </div>
          <button onClick={onClose} className="text-[#a0a0ab] hover:text-white"><X size={16} /></button>
        </div>
        <p className="text-[#a0a0ab] text-sm mb-1">{t('settings.deleteAccountConfirmText')}</p>
        <input
          type="email"
          value={emailInput}
          onChange={e => setEmailInput(e.target.value)}
          placeholder={t('settings.deleteAccountEmailPlaceholder')}
          data-testid="delete-email-confirm-input"
          className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm mt-2 mb-4 focus:outline-none focus:border-[#ff6b6b]/50"
        />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/10 text-[#e8e8e8] text-sm hover:bg-white/5 transition-colors">
            {i18n.language === 'en' ? 'Cancel' : 'Annuler'}
          </button>
          <button
            onClick={() => onConfirm(emailInput)}
            disabled={!valid || loading}
            data-testid="delete-confirm-btn"
            className="flex-1 py-2.5 rounded-xl bg-[#ff6b6b] text-white text-sm font-bold hover:bg-[#ff5252] disabled:opacity-40 transition-colors"
          >
            {loading ? t('settings.deleting') : t('settings.deleteAccountConfirmBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Cancel Subscription Modal ──────────────────────────────────────────────
function CancelSubModal({ onConfirm, onClose, loading, t }) {
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm" data-testid="cancel-sub-modal">
      <div className="w-full max-w-sm bg-[#1a1927] border border-white/10 rounded-2xl p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-[#ffc107]" />
            <h3 className="font-heading font-bold text-white">{t('settings.cancelSub')}</h3>
          </div>
          <button onClick={onClose} className="text-[#a0a0ab] hover:text-white"><X size={16} /></button>
        </div>
        <p className="text-[#a0a0ab] text-sm mb-4">{t('settings.cancelSubConfirm')}</p>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/10 text-[#e8e8e8] text-sm hover:bg-white/5 transition-colors">
            {i18n.language === 'en' ? 'Keep my plan' : 'Garder mon plan'}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            data-testid="cancel-sub-confirm-btn"
            className="flex-1 py-2.5 rounded-xl border border-[#ffc107]/40 text-[#ffc107] text-sm font-bold hover:bg-[#ffc107]/10 disabled:opacity-40 transition-colors"
          >
            {loading ? '...' : t('settings.cancelSub')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function Settings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const { reopenBanner } = useCookieConsent();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Profile form
  const [profileForm, setProfileForm] = useState({ name: '', bio: '' });
  const [profileSaving, setProfileSaving] = useState(false);

  // Password form
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // Notifications
  const [notifs, setNotifs] = useState({
    weekly_digest: true, new_suggestions: true,
    community_replies: true, mentor_reminders: true, streak_reminders: true,
  });
  const [notifSaving, setNotifSaving] = useState(false);

  // Subscription
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Privacy / RGPD
  const [downloading, setDownloading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [opposeSent, setOpposeSent] = useState(false);

  // Social disconnect
  const [socialLoading, setSocialLoading] = useState('');

  // ── Load settings ──────────────────────────────────────────────────────
  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/settings/me`, { withCredentials: true });
      setSettings(res.data);
      setProfileForm({ name: res.data.name || '', bio: res.data.bio || '' });
      setNotifs(res.data.notification_prefs || {
        weekly_digest: true, new_suggestions: true,
        community_replies: true, mentor_reminders: true, streak_reminders: true,
      });
    } catch {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // ── Profile save ──────────────────────────────────────────────────────
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    try {
      await axios.patch(`${API}/api/settings/profile`, {
        name: profileForm.name,
        bio: profileForm.bio,
      }, { withCredentials: true });
      await refreshUser();
      toast.success(t('settings.profileSaved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.serverError'));
    } finally {
      setProfileSaving(false);
    }
  };

  // ── Password save ─────────────────────────────────────────────────────
  const handleSavePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 6) { toast.error(t('settings.passwordMin')); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error(t('settings.passwordMismatch')); return; }
    setPwSaving(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, pwForm, { withCredentials: true });
      toast.success(t('settings.passwordSaved'));
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.serverError'));
    } finally {
      setPwSaving(false);
    }
  };

  // ── Language change ───────────────────────────────────────────────────
  const handleLangChange = async (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('kurso_lang', lang);
    try {
      await axios.patch(`${API}/api/settings/profile`, { preferredLanguage: lang }, { withCredentials: true });
    } catch { /* non-blocking */ }
  };

  // ── Notifications save ────────────────────────────────────────────────
  const handleNotifToggle = async (key, val) => {
    const next = { ...notifs, [key]: val };
    setNotifs(next);
    setNotifSaving(true);
    try {
      await axios.patch(`${API}/api/settings/notifications`, next, { withCredentials: true });
      toast.success(t('settings.notifSaved'));
    } catch { /* silent */ }
    finally { setNotifSaving(false); }
  };

  // ── Cancel subscription ───────────────────────────────────────────────
  const handleCancelSub = async () => {
    setCancelLoading(true);
    try {
      await axios.post(`${API}/api/settings/cancel-subscription`, {}, { withCredentials: true });
      toast.success(t('settings.cancelSubSuccess'));
      setShowCancelModal(false);
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.serverError'));
    } finally {
      setCancelLoading(false);
    }
  };

  // ── Download data ─────────────────────────────────────────────────────
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/api/settings/data`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `kurso_data_${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('errors.serverError'));
    } finally {
      setDownloading(false);
    }
  };

  // ── Delete account ────────────────────────────────────────────────────
  const handleDeleteAccount = async (emailConfirm) => {
    setDeleteLoading(true);
    try {
      const res = await axios.post(`${API}/api/settings/delete-account`,
        { email_confirm: emailConfirm }, { withCredentials: true });
      const dateStr = res.data.deletion_date?.slice(0, 10) || '';
      setShowDeleteModal(false);
      navigate('/');
      toast.success(t('settings.deleteSuccess', { date: dateStr }));
      logout();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.serverError'));
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── RGPD opposition ───────────────────────────────────────────────────
  const handleOppose = async () => {
    try {
      await axios.post(`${API}/api/settings/rgpd-opposition`, {}, { withCredentials: true });
      setOpposeSent(true);
      toast.success(t('settings.opposeSent'));
    } catch {
      toast.error(t('errors.serverError'));
    }
  };

  // ── Disconnect social ─────────────────────────────────────────────────
  const handleDisconnectSocial = async (provider) => {
    setSocialLoading(provider);
    try {
      const res = await axios.delete(`${API}/api/settings/social/${provider}`, { withCredentials: true });
      setSettings(s => ({ ...s, social_providers: res.data.providers }));
      toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} déconnecté`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.serverError'));
    } finally {
      setSocialLoading('');
    }
  };

  if (!user) { navigate('/auth'); return null; }

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#ff6b6b] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const planColor = PLAN_COLORS[settings?.plan] || '#a0a0ab';
  const isFreePlan = !settings?.plan || settings.plan === 'free';
  const isEN = i18n.language === 'en';

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4" data-testid="settings-page">
        <h1 className="font-heading text-2xl font-bold text-white" data-testid="settings-title">
          {t('settings.pageTitle')}
        </h1>

        {/* ── 1. MON PROFIL ─────────────────────────────────────────── */}
        <Section icon={User} title={t('settings.sectionProfile')} testId="settings-profile-section">
          <form onSubmit={handleSaveProfile} className="space-y-3">
            <div>
              <label className="text-xs text-[#a0a0ab] mb-1.5 block">{t('settings.displayName')}</label>
              <input
                type="text"
                value={profileForm.name}
                onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}
                placeholder={t('settings.displayNamePlaceholder')}
                data-testid="settings-name-input"
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
              />
            </div>
            <div>
              <label className="text-xs text-[#a0a0ab] mb-1.5 block">{t('settings.bio')}</label>
              <textarea
                value={profileForm.bio}
                onChange={e => setProfileForm(f => ({ ...f, bio: e.target.value }))}
                placeholder={t('settings.bioPlaceholder')}
                rows={3}
                data-testid="settings-bio-input"
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50 resize-none"
              />
            </div>
            <button type="submit" disabled={profileSaving} data-testid="settings-save-profile-btn"
              className="btn-primary py-2.5 px-5 text-sm disabled:opacity-50">
              {profileSaving ? t('settings.savingProfile') : t('settings.saveProfile')}
            </button>
          </form>
        </Section>

        {/* ── 2. SÉCURITÉ ───────────────────────────────────────────── */}
        <Section icon={Lock} title={t('settings.sectionSecurity')} testId="settings-security-section">
          {/* Password form */}
          {(settings?.has_password || !settings?.social_providers?.length) && (
            <form onSubmit={handleSavePassword} className="space-y-3 mb-5">
              {settings?.has_password && (
                <div>
                  <label className="text-xs text-[#a0a0ab] mb-1.5 block">{t('settings.currentPassword')}</label>
                  <input type={showPw ? 'text' : 'password'} value={pwForm.current_password}
                    onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))}
                    data-testid="settings-current-pw" placeholder="••••••••"
                    className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
                  />
                </div>
              )}
              {!settings?.has_password && (
                <p className="text-xs text-[#a0a0ab] mb-2">{t('profile.socialPasswordHint')}</p>
              )}
              <div className="relative">
                <label className="text-xs text-[#a0a0ab] mb-1.5 block">{t('settings.newPassword')}</label>
                <input type={showPw ? 'text' : 'password'} value={pwForm.new_password}
                  onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))}
                  data-testid="settings-new-pw" placeholder={t('settings.passwordMin')}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 pr-10 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
                />
                <button type="button" onClick={() => setShowPw(s => !s)}
                  className="absolute right-3 bottom-2.5 text-[#a0a0ab] hover:text-white">
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <div>
                <label className="text-xs text-[#a0a0ab] mb-1.5 block">{t('settings.confirmPassword')}</label>
                <input type={showPw ? 'text' : 'password'} value={pwForm.confirm_password}
                  onChange={e => setPwForm(f => ({ ...f, confirm_password: e.target.value }))}
                  data-testid="settings-confirm-pw" placeholder="••••••••"
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
                />
              </div>
              <button type="submit" disabled={pwSaving} data-testid="settings-save-pw-btn"
                className="btn-primary py-2.5 px-5 text-sm disabled:opacity-50">
                {pwSaving ? t('settings.savingPassword') : t('settings.savePassword')}
              </button>
            </form>
          )}

          {/* Connected social accounts */}
          <div className="mt-2">
            <p className="text-xs text-[#a0a0ab] mb-3 font-medium uppercase tracking-wider">{t('settings.socialAccountsTitle')}</p>
            <div className="space-y-2">
              {SOCIAL_PROVIDERS.map(({ key, label, icon }) => {
                const isConnected = settings?.social_providers?.includes(key);
                return (
                  <div key={key} className="flex items-center justify-between py-2.5 px-3 rounded-xl bg-white/3 border border-white/5">
                    <div className="flex items-center gap-2.5">
                      <span className="text-base">{icon}</span>
                      <span className="text-sm text-white">{label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${isConnected ? 'bg-[#20c997]/10 text-[#20c997]' : 'bg-white/5 text-[#a0a0ab]'}`}>
                        {isConnected ? t('settings.connected') : t('settings.notConnected')}
                      </span>
                    </div>
                    {isConnected && (
                      <button
                        onClick={() => handleDisconnectSocial(key)}
                        disabled={socialLoading === key}
                        data-testid={`disconnect-${key}-btn`}
                        className="text-xs text-[#a0a0ab] hover:text-[#ff6b6b] transition-colors disabled:opacity-50"
                      >
                        {socialLoading === key ? '...' : t('settings.disconnect')}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Section>

        {/* ── 3. PRÉFÉRENCES ────────────────────────────────────────── */}
        <Section icon={Globe} title={t('settings.sectionPreferences')} testId="settings-prefs-section">
          {/* Language */}
          <div className="mb-5">
            <p className="text-sm text-[#e8e8e8] mb-2">{t('settings.language')}</p>
            <div className="flex gap-2">
              {['fr', 'en'].map(lang => (
                <button
                  key={lang}
                  onClick={() => handleLangChange(lang)}
                  data-testid={`lang-btn-${lang}`}
                  className={`flex-1 py-2.5 rounded-xl border text-sm font-semibold transition-all ${
                    i18n.language === lang
                      ? 'border-[#ff6b6b] bg-[#ff6b6b]/10 text-[#ff6b6b]'
                      : 'border-white/10 text-[#a0a0ab] hover:border-white/20 hover:text-white'
                  }`}
                >
                  {lang === 'fr' ? '🇫🇷 Français' : '🇬🇧 English'}
                </button>
              ))}
            </div>
          </div>

          {/* Email notifications */}
          <p className="text-sm text-[#e8e8e8] mb-3">{t('settings.notifTitle')}</p>
          <div className="space-y-3">
            {[
              { key: 'weekly_digest', label: t('settings.notifWeeklyDigest') },
              { key: 'new_suggestions', label: t('settings.notifSuggestions') },
              { key: 'community_replies', label: t('settings.notifCommunity') },
              { key: 'mentor_reminders', label: t('settings.notifMentor') },
              { key: 'streak_reminders', label: t('settings.notifStreak') },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-[#a0a0ab]">{label}</span>
                <Toggle
                  checked={!!notifs[key]}
                  onChange={val => handleNotifToggle(key, val)}
                  testId={`notif-toggle-${key}`}
                />
              </div>
            ))}
          </div>
        </Section>

        {/* ── 4. ABONNEMENT ─────────────────────────────────────────── */}
        <Section icon={CreditCard} title={t('settings.sectionSubscription')} testId="settings-subscription-section">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-bold px-2.5 py-1 rounded-full border"
              style={{ color: planColor, borderColor: `${planColor}40`, backgroundColor: `${planColor}15` }}>
              {settings?.plan?.charAt(0).toUpperCase() + (settings?.plan?.slice(1) || '')}
            </span>
            {settings?.subscription_cancel_at_period_end && (
              <span className="text-xs text-[#ffc107] flex items-center gap-1">
                <AlertTriangle size={12} /> {t('settings.cancelAtEnd')}
              </span>
            )}
          </div>

          {!isFreePlan && settings?.subscription_current_period_end && (
            <p className="text-xs text-[#a0a0ab] mb-4">
              {t('settings.nextBilling')} : {new Date(settings.subscription_current_period_end * 1000).toLocaleDateString(isEN ? 'en-GB' : 'fr-FR')}
            </p>
          )}

          {isFreePlan && (
            <p className="text-sm text-[#a0a0ab] mb-4">{t('settings.freePlanNote')}</p>
          )}

          <div className="flex gap-2 flex-wrap">
            <button onClick={() => navigate('/pricing')} data-testid="settings-upgrade-btn"
              className="btn-primary py-2.5 px-5 text-sm flex items-center gap-1.5">
              {t('settings.upgradeBtn')} <ChevronRight size={14} />
            </button>
            {!isFreePlan && !settings?.subscription_cancel_at_period_end && (
              <button onClick={() => setShowCancelModal(true)} data-testid="settings-cancel-sub-btn"
                className="py-2.5 px-5 text-sm rounded-xl border border-white/10 text-[#a0a0ab] hover:text-[#ffc107] hover:border-[#ffc107]/30 transition-colors">
                {t('settings.cancelSub')}
              </button>
            )}
          </div>
        </Section>

        {/* ── 5. CONFIDENTIALITÉ & RGPD ─────────────────────────────── */}
        <Section icon={Shield} title={t('settings.sectionPrivacy')} testId="settings-privacy-section">
          <div className="space-y-3">
            {/* Cookies */}
            <button onClick={reopenBanner} data-testid="settings-manage-cookies-btn"
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-white/8 hover:bg-white/5 transition-colors text-left group">
              <div>
                <p className="text-sm text-white font-medium">{t('settings.manageCookiesBtn')}</p>
              </div>
              <ChevronRight size={16} className="text-[#a0a0ab] group-hover:text-white transition-colors" />
            </button>

            {/* Download data */}
            <div className="px-4 py-3 rounded-xl border border-white/8">
              <div className="flex items-start justify-between mb-1">
                <p className="text-sm text-white font-medium">{t('settings.downloadDataBtn')}</p>
                <button onClick={handleDownload} disabled={downloading} data-testid="settings-download-data-btn"
                  className="flex items-center gap-1.5 text-xs text-[#ff6b6b] hover:text-[#ff5252] transition-colors disabled:opacity-50 shrink-0 ml-3">
                  <Download size={13} />
                  {downloading ? t('settings.downloading') : (isEN ? 'Download' : 'Télécharger')}
                </button>
              </div>
              <p className="text-xs text-[#a0a0ab]">{t('settings.downloadDataDesc')}</p>
            </div>

            {/* Edit personal data */}
            <div className="px-4 py-3 rounded-xl border border-white/8">
              <p className="text-sm text-white font-medium mb-1">{t('settings.editPersonalData')}</p>
              <p className="text-xs text-[#a0a0ab] mb-2">{t('settings.editPersonalDataDesc')}</p>
              <button
                onClick={() => navigate('/contact')}
                data-testid="settings-edit-personal-data-btn"
                className="text-xs text-[#ff6b6b] hover:underline flex items-center gap-1"
              >
                {isEN ? 'Contact us' : 'Nous contacter'} <ExternalLink size={11} />
              </button>
            </div>

            {/* Oppose processing */}
            <div className="px-4 py-3 rounded-xl border border-white/8">
              <p className="text-sm text-white font-medium mb-1">{t('settings.opposeProcessing')}</p>
              <p className="text-xs text-[#a0a0ab] mb-2">{t('settings.opposeProcessingDesc')}</p>
              {opposeSent ? (
                <p className="text-xs text-[#20c997] flex items-center gap-1"><Check size={12} /> {t('settings.opposeSent')}</p>
              ) : (
                <button onClick={handleOppose} data-testid="settings-oppose-btn"
                  className="text-xs text-[#ffc107] hover:underline">
                  {t('settings.opposeProcessing')}
                </button>
              )}
            </div>

            {/* DPO contact */}
            <div className="px-4 py-3 rounded-xl bg-white/3 border border-white/5">
              <p className="text-xs text-[#a0a0ab] mb-1">{t('settings.dpoContact')}</p>
              <a href="mailto:privacy@mykurso.fr" className="text-xs text-[#ff6b6b] hover:underline flex items-center gap-1">
                privacy@mykurso.fr <ExternalLink size={10} />
              </a>
            </div>

            {/* Legal links */}
            <div className="flex gap-3 flex-wrap pt-1">
              <Link to={isEN ? '/en/privacy-policy' : '/politique-de-confidentialite'}
                className="text-xs text-[#a0a0ab] hover:text-white transition-colors flex items-center gap-1">
                {t('settings.privacyPolicyLink')} <ExternalLink size={10} />
              </Link>
              <Link to={isEN ? '/en/personal-data' : '/donnees-personnelles'}
                className="text-xs text-[#a0a0ab] hover:text-white transition-colors flex items-center gap-1">
                {t('settings.personalDataLink')} <ExternalLink size={10} />
              </Link>
            </div>

            {/* Delete account — danger zone */}
            <div className="mt-2 pt-4 border-t border-white/5">
              <p className="text-xs text-[#a0a0ab] mb-2">{t('settings.deleteAccountDesc')}</p>
              <button onClick={() => setShowDeleteModal(true)} data-testid="settings-delete-account-btn"
                className="flex items-center gap-2 text-sm text-[#ff6b6b]/60 hover:text-[#ff6b6b] transition-colors">
                <AlertTriangle size={14} /> {t('settings.deleteAccountBtn')}
              </button>
            </div>
          </div>
        </Section>

        {/* ── 6. LOGOUT ─────────────────────────────────────────────── */}
        <div className="pb-6">
          <button
            onClick={() => { navigate('/'); toast.success(t('nav.logoutToast')); logout(); }}
            data-testid="settings-logout-btn"
            className="w-full flex items-center justify-center gap-2.5 py-3 rounded-2xl border border-[#ff6b6b]/20 text-[#ff6b6b]/70 hover:text-[#ff6b6b] hover:bg-[#ff6b6b]/5 hover:border-[#ff6b6b]/40 transition-all text-sm font-medium"
          >
            <LogOut size={16} /> {t('nav.logout')}
          </button>
        </div>
      </div>

      {/* Modals */}
      {showCancelModal && (
        <CancelSubModal
          onConfirm={handleCancelSub}
          onClose={() => setShowCancelModal(false)}
          loading={cancelLoading}
          t={t}
        />
      )}
      {showDeleteModal && (
        <DeleteAccountModal
          email={settings?.email || user?.email || ''}
          onConfirm={handleDeleteAccount}
          onClose={() => setShowDeleteModal(false)}
          loading={deleteLoading}
          t={t}
        />
      )}
    </Layout>
  );
}
